



import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import { FaFacebookF, FaInstagram, FaLinkedin, FaWhatsapp } from "react-icons/fa";

import bslogo from "./assets/companylogo.png";
import csimg from "./assets/csimg.jpg";
import dsimg from "./assets/dsimg.jpg";
import feimg from "./assets/feimg.jpg";
import image3 from "./assets/image3.jpg";

import drone1 from "../src/assets/drone1.png";
import drone2 from "../src/assets/drone2.png";
import bank from "../src/assets/bank.png";

import ThreeD from "./ThreeD";

// ── Animated counter hook ──────────────────────────────────────────────────
function useCounter(target, duration = 1200, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

// ── Split text into animated spans ────────────────────────────────────────
function SplitText({ text, className, delay = 0 }) {
  return (
    <span className={className} aria-label={text}>
      {text.split("").map((char, i) => (
        <span
          key={i}
          className="char-animate"
          style={{ animationDelay: `${delay + i * 0.04}s` }}
          aria-hidden="true"
        >
          {char === " " ? "\u00A0" : char}
        </span>
      ))}
    </span>
  );
}

function TopMediumSlider({ images, interval = 3500, textSlideIndex = 0 }) {
  const [active, setActive] = useState(0);
  const showText = active === textSlideIndex;

  useEffect(() => {
    if (!images || images.length < 2) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % images.length);
    }, interval);
    return () => clearInterval(timer);
  }, [images, interval]);

  if (!images || images.length === 0) return null;

  return (
    <section className="top-medium-slider">
      <div className="top-medium-slider-frame">
        {images.map((img, i) => (
          <img
            key={i}
            src={img}
            alt={`Bliss Sierra slide ${i + 1}`}
            className={`top-medium-slider-img${i === active ? " active" : ""}`}
          />
        ))}

        {showText && (
          <>
            <div className="top-medium-slider-overlay" />
            <div className="top-medium-slider-content">
              <p className="top-medium-slider-mini">
                BLISS SIERRA × INNOVATION
              </p>
              <h2 className="top-medium-slider-title">
                Smart Digital Services For Modern business
              </h2>
              <p className="top-medium-slider-text">
                Defence UAV collaboration, smart surveillance solutions,
                cybersecurity, web development, and modern digital services built
                for real impact.
              </p>
              <Link to="/Services" className="top-medium-slider-btn">
                Explore Services
              </Link>
            </div>
          </>
        )}

        <div className="top-medium-slider-counter">
          {active + 1} / {images.length}
        </div>

        <div className="top-medium-slider-dots">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`top-medium-slider-dot${i === active ? " active" : ""}`}
              onClick={() => setActive(i)}
              aria-label={`Show slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

const BlissSierra = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [processVisible, setProcessVisible] = useState(false);
  const processRef = useRef(null);

  useEffect(() => {
    const revealTargets = document.querySelectorAll(
      ".reveal-up, .reveal-left, .reveal-right, .title-glow, .stagger-child"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.12 }
    );
    revealTargets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const btns = document.querySelectorAll(".serum-btn, .apply-btn");
    if (!btns.length) return;
    const handleMove = (e) => {
      const btn = e.currentTarget;
      const rect = btn.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;
      btn.style.transform = `translate(${x * 0.28}px, ${y * 0.28}px) scale(1.04)`;
    };
    const handleLeave = (e) => {
      e.currentTarget.style.transform = "";
    };
    btns.forEach((btn) => {
      btn.addEventListener("mousemove", handleMove);
      btn.addEventListener("mouseleave", handleLeave);
    });
    return () => {
      btns.forEach((btn) => {
        btn.removeEventListener("mousemove", handleMove);
        btn.removeEventListener("mouseleave", handleLeave);
      });
    };
  }, []);

  const serviceCards = [
    {
      title: "Mobile Application Development",
      text: "Modern, responsive, and scalable mobile applications built for Android and iOS with smooth user experience, secure backend integration, and production-ready performance.",
      image: image3,
      icon: "📱",
    },
    {
      title: "Web Development",
      text: "Fast, responsive, scalable websites and platforms built for performance, usability, and long-term business growth.",
      image: dsimg,
      icon: "🌐",
    },
    {
      title: "Frontend Development",
      text: "Interactive, premium user interfaces crafted with strong usability, accessibility, and smooth digital experiences.",
      image: feimg,
      icon: "✨",
    },
    {
      title: "Programming & JavaScript",
      text: "Robust application logic, dynamic workflows, modern integrations, and scalable code architecture tailored to your goals.",
      image: csimg,
      icon: "⚡",
    },
  ];

  const tungaDrones = [
    { model: "Drishya Lite", endurance: "25 min", range: "3 km", category: "Light Surveillance" },
    { model: "Pro MK III", endurance: "100 min", range: "10 km", category: "Advanced" },
    { model: "Pro Plus", endurance: "75 min", range: "10 km", category: "High Altitude" },
    { model: "Mini MK II", endurance: "70 min", range: "5 km", category: "Surveillance" },
  ];

  const bankingProducts = [
    { title: "Open Banking Platform", desc: "FAPI & PSD2 compliant infrastructure with real-time consent management and secure data sharing." },
    { title: "TPP Platform", desc: "Third-party provider onboarding with mTLS, JWT auth, and full regulatory compliance." },
    { title: "Account Aggregation", desc: "Multi-bank data aggregation for unified views, enriched transactions, and corporate flows." },
    { title: "AI Banking Agents", desc: "Intelligent agents for automated KYC, fraud detection, NLP support, and regulatory reporting." },
    { title: "Security & Compliance", desc: "ISO 27001 certified, FAPI-ready, PSD2 compliant with enterprise-grade security protocols." },
    { title: "Rapid Deployment", desc: "Pre-built modules and proven patterns deliver banking solutions in weeks, not months." },
  ];

  const sliderImages = [dsimg, drone1, bank];

  return (
    <div className="services-page-root">
      <style>{`
        html, body { cursor: auto !important; }
        input, textarea { cursor: text !important; caret-color: #a3e635; }
        button, a, select, .apply-btn, .hero-btn, .close { cursor: pointer !important; }
        canvas { pointer-events: none; }

        * { box-sizing: border-box; margin: 0; padding: 0; }

        :root {
          --bg-1: #0a0e1a;
          --bg-2: #0a0e1a;
          --bg-3: #0a0e1a;
          --text-main: #eef2f7;
          --text-soft: #94a3b8;
          --text-muted: #a8b3c4;
          --accent: #a3e635;
          --accent-2: #bef264;
          --accent-deep: #84cc16;
          --line-soft: rgba(163, 230, 53, 0.16);
          --card-bg: rgba(19, 26, 44, 1);
          --card-bg-2: rgba(19, 26, 44, 1);
          --shadow-orange: 0 16px 40px rgba(163, 230, 53, 0.16);
          --shadow-soft: 0 18px 45px rgba(15, 23, 42, 0.08);
          --shadow-card: 0 14px 35px rgba(15, 23, 42, 0.09);
          --heading-font: "Cinzel", serif;
          --body-font: "Manrope", sans-serif;
        }

        html { scroll-behavior: smooth; }

        body {
          font-family: var(--body-font);
          color: var(--text-main);
          background: #0a0e1a;
          overflow-x: hidden;
        }

        a { text-decoration: none; color: inherit; }

        .char-animate {
          display: inline-block;
          opacity: 0;
          transform: translateY(60px) rotateX(-90deg);
          animation: charReveal 0.65s cubic-bezier(0.22,1,0.36,1) forwards;
        }

        @keyframes charReveal {
          to { opacity: 1; transform: translateY(0) rotateX(0deg); }
        }

        .services-page-root {
          position: relative;
          min-height: 100vh;
          overflow: hidden;
          background: #0a0e1a;
        }

        @keyframes glowPulse {
          0%,100% { text-shadow: 0 0 12px rgba(163, 230, 53,0.10); }
          50% { text-shadow: 0 0 28px rgba(163, 230, 53,0.30); }
        }

        @keyframes fadeUp {
          from { opacity:0; transform:translateY(45px); }
          to { opacity:1; transform:translateY(0); }
        }

        .services-navbar,
        .top-medium-slider,
        .services-section,
        .services-grid-wrap,
        .process-section,
        .tunga-section-minimal,
        .banking-section-minimal,
        .services-footer {
          position: relative;
          z-index: 1;
        }

        /* ── NAVBAR ── */
        .services-navbar {
          position: sticky;
          top: 0;
          z-index: 1000;
          background: rgba(19, 26, 44, 0.88);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border-bottom: 1px solid rgba(163, 230, 53,0.12);
          box-shadow: 0 8px 28px rgba(15,23,42,0.05);
          transition: box-shadow 0.4s ease, background 0.4s ease;
          will-change: background, box-shadow;
          transform: translateZ(0);
        }

        .services-navbar.scrolled {
          background: rgba(19, 26, 44, 0.98);
          box-shadow: 0 12px 40px rgba(15,23,42,0.14), 0 1px 0 rgba(163, 230, 53,0.2);
        }

        .services-nav-container {
          width: min(1220px, 92%);
          margin: 0 auto;
          padding: 14px 0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .services-nav-logo {
          display: flex;
          align-items: center;
          gap: 14px;
          transition: transform 0.3s ease;
        }

        .services-nav-logo img {
          width: 54px;
          height: 54px;
          object-fit: contain;
          border-radius: 14px;
          filter: drop-shadow(0 8px 18px rgba(163, 230, 53,0.18));
          transition: transform 0.4s cubic-bezier(0.34,1.56,0.64,1), filter 0.3s ease;
        }

        .services-nav-logo:hover img {
          transform: perspective(300px) rotateY(16deg) rotateX(-6deg) scale(1.1);
          filter: drop-shadow(0 14px 26px rgba(163, 230, 53,0.38));
        }

        .site-title {
          font-family: var(--heading-font);
          font-size: 1.45rem;
          font-weight: 800;
          letter-spacing: 1px;
          color: #eef2f7;
        }

        .back-button {
          width: 42px;
          height: 42px;
          border-radius: 50%;
          border: none;
          background: linear-gradient(135deg, var(--accent-deep), var(--accent-2));
          color: #fff;
          font-size: 18px;
          cursor: pointer;
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
          box-shadow: 0 10px 24px rgba(163, 230, 53,0.22);
        }

        .back-button:hover {
          transform: perspective(300px) rotateY(-18deg) translateZ(8px) scale(1.12);
          box-shadow: 0 18px 38px rgba(163, 230, 53,0.35);
        }

        .services-nav-links {
          display: flex;
          align-items: center;
          gap: 22px;
        }

        .services-nav-links a {
          color: var(--text-soft);
          font-size: 0.96rem;
          font-weight: 700;
          position: relative;
          transition: color 0.28s ease;
        }

        .services-nav-links a:hover,
        .services-nav-links a.active { color: var(--accent-deep); }

        .services-nav-links a::after {
          content: "";
          position: absolute;
          left: 0;
          bottom: -7px;
          width: 0%;
          height: 2px;
          border-radius: 999px;
          background: linear-gradient(90deg, var(--accent), var(--accent-2));
          box-shadow: 0 0 14px rgba(163, 230, 53,0.28);
          transition: width 0.3s ease;
        }

        .services-nav-links a:hover::after,
        .services-nav-links a.active::after { width: 100%; }

        .nav-toggle {
          display: none;
          font-size: 1.8rem;
          color: #eef2f7;
          cursor: pointer;
        }

        /* ── TOP MEDIUM SLIDER ── */
        .top-medium-slider {
          position: relative;
          z-index: 1;
          width: min(1220px, 92%);
          margin: 26px auto 0;
        }

        .top-medium-slider-frame {
          position: relative;
          width: 100%;
          height: clamp(220px, 42vw, 580px);
          overflow: hidden;
          border-radius: 30px;
          background: #0a0e1a;
          border: 1px solid rgba(163, 230, 53, 0.16);
          box-shadow: 0 20px 50px rgba(15, 23, 42, 0.16);
        }

        .top-medium-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          opacity: 0;
          transform: scale(1.06);
          transition: opacity 0.95s cubic-bezier(0.4, 0, 0.2, 1), transform 4.5s ease;
        }

        .top-medium-slider-img.active { opacity: 1; transform: scale(1); }

        .top-medium-slider-overlay {
          position: absolute;
          inset: 0;
          z-index: 1;
          background:
            radial-gradient(circle at 18% 30%, rgba(163, 230, 53, 0.22), transparent 32%),
            linear-gradient(90deg, rgba(0,0,0,0.76) 0%, rgba(0,0,0,0.48) 48%, rgba(0,0,0,0.14) 100%),
            linear-gradient(180deg, rgba(0,0,0,0.04), rgba(0,0,0,0.36));
        }

        .top-medium-slider-content {
          position: absolute;
          z-index: 2;
          left: 58px;
          bottom: 58px;
          max-width: 570px;
          color: #fff;
          animation: fadeUp 0.8s ease forwards;
        }

        .top-medium-slider-mini {
          display: inline-block;
          font-size: 0.74rem;
          font-weight: 900;
          letter-spacing: 3px;
          color: var(--accent-2);
          margin-bottom: 13px;
        }

        .top-medium-slider-title {
          font-family: var(--heading-font);
          font-size: clamp(2rem, 4vw, 3.75rem);
          line-height: 1.05;
          letter-spacing: 0.7px;
          margin-bottom: 14px;
          text-shadow: 0 14px 32px rgba(0,0,0,0.42);
        }

        .top-medium-slider-text {
          color: rgba(255,255,255,0.9);
          font-size: 0.96rem;
          line-height: 1.75;
          max-width: 520px;
          margin-bottom: 22px;
        }

        .top-medium-slider-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 175px;
          padding: 13px 25px;
          border-radius: 999px;
          color: #ffffff;
          font-weight: 900;
          letter-spacing: 0.4px;
          background: linear-gradient(135deg, var(--accent-deep), var(--accent-2));
          box-shadow: 0 14px 32px rgba(163, 230, 53,0.34);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .top-medium-slider-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 20px 42px rgba(163, 230, 53,0.44);
        }

        .top-medium-slider-counter {
          position: absolute;
          z-index: 4;
          right: 28px;
          bottom: 22px;
          background: rgba(0,0,0,0.48);
          color: #fff;
          font-size: 0.76rem;
          font-weight: 800;
          letter-spacing: 1px;
          padding: 5px 13px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,0.12);
          backdrop-filter: blur(8px);
        }

        .top-medium-slider-dots {
          position: absolute;
          z-index: 4;
          left: 50%;
          bottom: 28px;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          gap: 9px;
        }

        .top-medium-slider-dot {
          width: 31px;
          height: 5px;
          border-radius: 999px;
          border: none;
          background: rgba(19, 26, 44, 0.55);
          cursor: pointer;
          padding: 0;
          transition: background 0.25s ease, width 0.25s ease, transform 0.25s ease;
        }

        .top-medium-slider-dot.active {
          width: 52px;
          background: var(--accent-2);
          box-shadow: 0 0 18px rgba(163, 230, 53,0.55);
        }

        /* ── SECTIONS ── */
        .services-section,
        .services-grid-wrap,
        .process-section,
        .tunga-section-minimal,
        .banking-section-minimal,
        .services-footer {
          width: min(1220px, 92%);
          margin: 42px auto 0;
        }

        .intro-shell {
          padding: 36px 34px;
          border-radius: 28px;
          background: #0a0e1a;
          border: 1px solid rgba(163, 230, 53,0.14);
          box-shadow: var(--shadow-card);
        }

        .services-grid {
          margin-top: 24px;
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 22px;
          perspective: 1400px;
        }

        .service-card {
          background: #0a0e1a;
          border: 1px solid rgba(163, 230, 53,0.14);
          border-radius: 26px;
          padding: 18px;
          position: relative;
          overflow: hidden;
          box-shadow: var(--shadow-card);
          transform-style: preserve-3d;
          will-change: transform;
          transition: transform 0.18s ease, box-shadow 0.3s ease;
          cursor: default;
          opacity: 0;
          transform: perspective(800px) rotateX(16deg) translateY(38px);
        }

        .service-card.visible {
          animation: card3DReveal 0.75s cubic-bezier(0.22,1,0.36,1) forwards;
        }

        .service-card:nth-child(2) { animation-delay: 0.1s; }
        .service-card:nth-child(3) { animation-delay: 0.2s; }
        .service-card:nth-child(4) { animation-delay: 0.3s; }

        @keyframes card3DReveal {
          from { opacity:0; transform:perspective(800px) rotateX(16deg) rotateY(-8deg) translateY(38px) scale(0.95); }
          to { opacity:1; transform:perspective(800px) rotateX(0deg) rotateY(0deg) translateY(0) scale(1); }
        }

        .service-card::after {
          content: "";
          position: absolute;
          inset: auto -30% -60% auto;
          width: 160px;
          height: 160px;
          background: radial-gradient(circle, rgba(163, 230, 53,0.14), transparent 70%);
          pointer-events: none;
        }

        .service-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: -75%;
          width: 50%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(19, 26, 44, 0.35), transparent);
          transform: skewX(-15deg);
          pointer-events: none;
          z-index: 2;
        }

        .service-card:hover::before { animation: cardShimmer 0.7s ease forwards; }

        @keyframes cardShimmer { to { left: 130%; } }

        .service-card:hover {
          transform: perspective(900px) translateY(-10px) rotateX(3deg) scale(1.01) !important;
          box-shadow: 0 28px 50px rgba(163, 230, 53,0.16), var(--shadow-card) !important;
        }

        .card-icon {
          position: absolute;
          top: 14px;
          right: 14px;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: rgba(163, 230, 53,0.10);
          display: grid;
          place-items: center;
          font-size: 1.1rem;
          backdrop-filter: blur(6px);
          border: 1px solid rgba(163, 230, 53,0.16);
          z-index: 3;
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1), background 0.3s ease;
        }

        .service-card:hover .card-icon {
          transform: rotate(15deg) scale(1.2) translateZ(20px);
          background: rgba(163, 230, 53,0.20);
        }

        .service-image {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          margin-bottom: 16px;
        }

        .service-image img {
          width: 100%;
          aspect-ratio: 4 / 4.5;
          object-fit: cover;
          display: block;
          transition: transform 0.55s ease, filter 0.45s ease;
        }

        .service-card:hover .service-image img {
          transform: scale(1.07);
          filter: brightness(1.06) saturate(1.08);
        }

        .service-image::after {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(163, 230, 53,0.25), transparent 50%);
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .service-card:hover .service-image::after { opacity: 1; }

        .service-card h3,
        .process-point h3,
        .footer-col h3 {
          font-family: var(--heading-font);
          color: #eef2f7;
          font-size: 1.2rem;
          margin-bottom: 10px;
        }

        .section-heading {
          font-family: var(--heading-font);
          color: #eef2f7;
          font-size: clamp(2rem, 3vw, 3rem);
          margin-bottom: 18px;
          line-height: 1.1;
          letter-spacing: 0.8px;
        }

        .section-heading::after {
          content: "";
          display: block;
          width: 64px;
          height: 4px;
          background: linear-gradient(90deg, var(--accent), var(--accent-2));
          border-radius: 3px;
          margin-top: 16px;
        }

        .section-content p,
        .service-card p,
        .process-point p,
        .footer-col p,
        .footer-col li a {
          color: var(--text-soft);
          line-height: 1.9;
          font-size: 1rem;
        }

        .section-mini {
          display: inline-block;
          font-size: 0.78rem;
          font-weight: 800;
          letter-spacing: 3px;
          color: var(--accent-deep);
          margin-bottom: 12px;
        }

        .title-glow { animation: glowPulse 4s ease-in-out infinite; }

        /* ── TUNGA SECTION MINIMAL ── */
        .tunga-section-minimal {
          margin-bottom: 0;
        }

        .tunga-shell-minimal {
          border-radius: 28px;
          background: #0a0e1a;
          border: 1px solid rgba(163, 230, 53,0.14);
          box-shadow: var(--shadow-card);
          overflow: hidden;
          position: relative;
          padding: 28px 34px;
        }

        .tunga-badge,
        .banking-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          background: rgba(163, 230, 53,0.09);
          border: 1px solid rgba(163, 230, 53,0.28);
          border-radius: 999px;
          padding: 5px 14px;
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 3px;
          color: var(--accent-deep);
          margin-bottom: 12px;
        }

        .badge-dot {
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--accent);
          animation: ledBlink 1.6s ease-in-out infinite;
          box-shadow: 0 0 8px rgba(163, 230, 53,0.7);
        }

        @keyframes ledBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.2; }
        }

        .tunga-heading,
        .banking-heading {
          font-family: var(--heading-font);
          font-size: 1.8rem;
          color: #eef2f7;
          line-height: 1.2;
          margin-bottom: 12px;
          letter-spacing: 0.5px;
        }

        .tunga-heading span,
        .banking-heading span {
          background: linear-gradient(90deg, var(--accent-deep), var(--accent-2));
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .tunga-subtext,
        .banking-subtext {
          color: var(--text-soft);
          font-size: 0.92rem;
          line-height: 1.7;
          max-width: 700px;
          margin-bottom: 18px;
        }

        .tunga-apps {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 18px;
        }

        .tunga-app-pill {
          background: rgba(163, 230, 53,0.08);
          border: 1px solid rgba(163, 230, 53,0.22);
          border-radius: 999px;
          padding: 5px 12px;
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--accent-deep);
          letter-spacing: 0.5px;
          transition: background 0.25s ease, transform 0.25s ease;
        }

        .tunga-app-pill:hover { background: rgba(163, 230, 53,0.16); transform: translateY(-2px); }

        .tunga-table-wrap {
          margin-top: 12px;
          overflow-x: auto;
          border-radius: 16px;
          border: 1px solid rgba(163, 230, 53,0.14);
          background: #0a0e1a;
          box-shadow: 0 4px 16px rgba(163, 230, 53,0.06);
        }

        .tunga-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }

        .tunga-table thead tr {
          background: rgba(163, 230, 53,0.08);
        }

        .tunga-table thead th {
          padding: 10px 12px;
          text-align: left;
          font-family: var(--heading-font);
          font-size: 0.68rem;
          letter-spacing: 1.5px;
          color: var(--accent-deep);
          font-weight: 700;
          border-bottom: 1px solid rgba(163, 230, 53,0.12);
          white-space: nowrap;
        }

        .tunga-table tbody tr {
          border-bottom: 1px solid rgba(163, 230, 53,0.06);
          transition: background 0.2s ease;
        }

        .tunga-table tbody tr:last-child { border-bottom: none; }
        .tunga-table tbody tr:hover { background: rgba(163, 230, 53,0.04); }

        .tunga-table td {
          padding: 9px 12px;
          color: var(--text-soft);
          vertical-align: middle;
          font-size: 0.85rem;
        }

        .tunga-table td:first-child { font-weight: 700; color: var(--text-main); }

        .tunga-category-badge {
          display: inline-block;
          padding: 2px 9px;
          border-radius: 999px;
          font-size: 0.7rem;
          font-weight: 700;
          background: rgba(163, 230, 53,0.10);
          border: 1px solid rgba(163, 230, 53,0.18);
          color: var(--accent-deep);
        }

        /* ── BANKING SECTION MINIMAL ── */
        .banking-section-minimal {
          margin-bottom: 0;
        }

        .banking-shell-minimal {
          border-radius: 28px;
          background: #0a0e1a;
          border: 1px solid rgba(163, 230, 53,0.14);
          box-shadow: var(--shadow-card);
          overflow: hidden;
          position: relative;
          padding: 28px 34px;
        }

        .banking-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-top: 16px;
        }

        .banking-card {
          padding: 16px;
          background: rgba(163, 230, 53,0.04);
          border: 1px solid rgba(163, 230, 53,0.14);
          border-radius: 16px;
          transition: all 0.28s ease;
        }

        .banking-card:hover {
          background: rgba(163, 230, 53,0.08);
          transform: translateY(-3px);
          box-shadow: 0 10px 24px rgba(163, 230, 53,0.12);
        }

        .banking-card h4 {
          font-family: var(--heading-font);
          font-size: 0.9rem;
          color: #eef2f7;
          margin-bottom: 6px;
          font-weight: 700;
        }

        .banking-card p {
          font-size: 0.82rem;
          color: var(--text-soft);
          line-height: 1.6;
        }

        /* ── PROCESS ── */
        .process-grid {
          margin-top: 24px;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 22px;
          perspective: 1400px;
        }

        .process-point {
          padding: 28px 24px;
          border-radius: 24px;
          background: #0a0e1a;
          border: 1px solid rgba(163, 230, 53,0.14);
          box-shadow: var(--shadow-card);
          transform-style: preserve-3d;
          will-change: transform;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .process-point::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(163, 230, 53,0.04), transparent);
          opacity: 0;
          transition: opacity 0.35s ease;
        }

        .process-point:hover::before { opacity: 1; }

        .process-point:hover {
          transform: perspective(900px) translateY(-8px) rotateX(3deg);
          box-shadow: 0 28px 50px rgba(163, 230, 53,0.14), var(--shadow-card);
        }

        .process-number {
          width: 46px; height: 46px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          font-weight: 800;
          color: white;
          margin-bottom: 16px;
          background: linear-gradient(135deg, var(--accent-deep), var(--accent-2));
          box-shadow: 0 10px 24px rgba(163, 230, 53,0.22);
          transition: transform 0.55s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
        }

        .process-point:hover .process-number {
          transform: rotateY(360deg) scale(1.18);
          box-shadow: 0 0 34px rgba(163, 230, 53,0.55);
        }

        /* ── REVEAL ANIMATIONS ── */
        .reveal-up {
          opacity: 0;
          transform: perspective(800px) rotateX(16deg) translateY(38px);
          transition: opacity 0.85s cubic-bezier(0.22,1,0.36,1), transform 0.85s cubic-bezier(0.22,1,0.36,1);
        }

        .reveal-up.visible {
          opacity: 1;
          transform: perspective(800px) rotateX(0deg) translateY(0);
          animation: none;
        }

        .reveal-left {
          opacity: 0;
          transform: perspective(800px) rotateY(-22deg) translateX(-55px);
          transition: opacity 0.85s cubic-bezier(0.22,1,0.36,1), transform 0.85s cubic-bezier(0.22,1,0.36,1);
        }

        .reveal-left.visible {
          opacity: 1;
          transform: perspective(800px) rotateY(0deg) translateX(0);
          animation: none;
        }

        .reveal-right {
          opacity: 0;
          transform: perspective(800px) rotateY(22deg) translateX(55px);
          transition: opacity 0.85s cubic-bezier(0.22,1,0.36,1), transform 0.85s cubic-bezier(0.22,1,0.36,1);
        }

        .reveal-right.visible {
          opacity: 1;
          transform: perspective(800px) rotateY(0deg) translateX(0);
          animation: none;
        }

        .process-grid .process-point:nth-child(2) { transition-delay: 0.1s; }
        .process-grid .process-point:nth-child(3) { transition-delay: 0.2s; }

        /* ── FOOTER ── */
        .services-footer { margin-bottom: 34px; }

        .footer-container {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 60px;
          align-items: start;
          background: #0a0e1a;
          border: 1px solid rgba(163, 230, 53,0.14);
          border-radius: 28px;
          padding: 34px;
          box-shadow: var(--shadow-card);
        }

        .footer-col {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          transition: transform 0.35s ease;
        }

        .footer-col:hover { transform: perspective(700px) translateZ(12px); }

        .footer-col ul {
          list-style: none;
          padding: 0; margin: 0;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 10px;
        }

        .footer-col li { width: 100%; margin: 0; }

        .footer-col li a {
          display: inline-block;
          transition: color 0.25s ease, transform 0.25s ease;
        }

        .footer-col li a:hover { color: var(--accent-deep); transform: translateX(6px); }

        .social-links {
          display: flex;
          gap: 14px;
          margin-top: 16px;
          flex-wrap: wrap;
        }

        .social-links a {
          width: 42px; height: 42px;
          border-radius: 50%;
          display: grid;
          place-items: center;
          color: var(--accent-deep);
          background: rgba(163, 230, 53,0.08);
          border: 1px solid rgba(163, 230, 53,0.14);
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1), background 0.28s ease, box-shadow 0.28s ease, color 0.28s ease;
        }

        .social-links a:hover {
          transform: perspective(300px) rotateX(-14deg) translateY(-8px) translateZ(14px);
          color: #ffffff;
          background: linear-gradient(135deg, var(--accent-deep), var(--accent-2));
          box-shadow: 0 16px 30px rgba(163, 230, 53,0.28);
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 1100px) {
          .services-grid { grid-template-columns: repeat(2, 1fr); }
          .process-grid, .footer-container { grid-template-columns: 1fr; }
          .banking-grid { grid-template-columns: repeat(2, 1fr); }
        }

        @media (max-width: 768px) {
          .top-medium-slider-frame { border-radius: 24px; }
          .top-medium-slider-content { left: 24px; right: 24px; bottom: 54px; max-width: none; }
          .top-medium-slider-title { font-size: 2rem; }
          .top-medium-slider-text { font-size: 0.9rem; line-height: 1.65; }
          .nav-toggle { display: block; }
          .services-nav-links {
            position: absolute;
            top: 82px; right: 4%;
            width: 240px;
            display: none;
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
            padding: 20px;
            background: rgba(19, 26, 44, 0.98);
            border-radius: 18px;
            border: 1px solid rgba(163, 230, 53,0.14);
            box-shadow: 0 18px 40px rgba(15,23,42,0.12);
          }
          .services-nav-links.active { display: flex; }
          .section-heading { font-size: 1.8rem; }
          .services-grid, .process-grid, .footer-container { grid-template-columns: 1fr; }
          .banking-grid { grid-template-columns: 1fr; }
          .intro-shell { padding: 24px 22px; }
          .tunga-shell-minimal { padding: 20px 22px; }
          .banking-shell-minimal { padding: 20px 22px; }
          .footer-container { padding: 26px; gap: 34px; }
        }

        @media (max-width: 480px) {
          .top-medium-slider-frame { border-radius: 20px; }
          .top-medium-slider-title { font-size: 1.55rem; }
          .top-medium-slider-text { display: none; }
          .top-medium-slider-btn { min-width: auto; padding: 11px 18px; font-size: 0.85rem; }
          .top-medium-slider-counter { display: none; }
          .site-title { font-size: 1.15rem; }
          .services-nav-logo img { width: 46px; height: 46px; }
          .banking-grid { grid-template-columns: 1fr; }
        }

        @media (prefers-reduced-motion: reduce) {
          .reveal-up, .reveal-left, .reveal-right {
            transform: none !important;
            transition: opacity 0.4s ease !important;
          }
          .char-animate { animation: none !important; opacity: 1 !important; transform: none !important; }
        }

        /* ── UNIFIED SITE FOOTER (matches Tunga Drones page) ── */
        .tunga-footer {
          background: linear-gradient(180deg, rgba(163, 230, 53, 0.08), #070a12 18%);
          color: white;
          padding: 60px 5%;
          margin-top: 80px;
          border-top: 3px solid var(--accent);
        }

        .tunga-footer-content {
          width: min(1220px, 92%);
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 50px;
          margin-bottom: 40px;
        }

        .tunga-footer-col h3 {
          font-family: var(--heading-font);
          font-size: 1.3rem;
          margin-bottom: 20px;
          color: var(--accent-2);
        }

        .tunga-footer-col p, .tunga-footer-col a {
          font-size: 0.95rem;
          line-height: 1.8;
          color: rgba(255,255,255,0.8);
          text-decoration: none;
        }

        .tunga-footer-col a:hover { color: var(--accent-2); }

        .tunga-social-links {
          display: flex;
          gap: 12px;
          margin-top: 20px;
        }

        .tunga-social-links a {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: rgba(163, 230, 53,0.2);
          display: grid;
          place-items: center;
          transition: all 0.3s ease;
          color: var(--accent-2);
        }

        .tunga-social-links a:hover {
          background: var(--accent);
          color: white;
          transform: translateY(-4px);
        }
      `}</style>

      <ThreeD />

      {/* ── NAVBAR ── */}
      <header className={`services-navbar${navScrolled ? " scrolled" : ""}`}>
        <div className="services-nav-container">
          <div className="services-nav-logo">
          
            <img src={bslogo} alt="Bliss Sierra Logo" />
            <h1 className="site-title">Bliss Sierra</h1>
          </div>

          <div className="nav-toggle" onClick={() => setMenuOpen(!menuOpen)}>☰</div>

          <nav className={`services-nav-links${menuOpen ? " active" : ""}`}>
            <NavLink to="/" onClick={() => setMenuOpen(false)}>Home</NavLink>
            <NavLink to="/Tunga-Drones" onClick={() => setMenuOpen(false)}>Tunga Drones</NavLink>
            <NavLink to="/Our-Projects" onClick={() => setMenuOpen(false)}>Our Projects</NavLink>
            <NavLink to="/Services" onClick={() => setMenuOpen(false)}>Services</NavLink>
            <NavLink to="/Careers" onClick={() => setMenuOpen(false)}>Careers</NavLink>
            <NavLink to="/OurTeam" onClick={() => setMenuOpen(false)}>Our Core Team</NavLink>
          </nav>
        </div>
      </header>

      {/* ── SLIDER WITH 3 IMAGES (TEXT ON FIRST SLIDE ONLY) ── */}
      <TopMediumSlider images={sliderImages} interval={3500} textSlideIndex={0} />

      {/* ── WHAT WE OFFER ── */}
      <section className="services-section">
        <div className="intro-shell">
          <div className="section-content reveal-up">
            <p className="section-mini">OUR EXPERTISE</p>
            <h2 className="section-heading title-glow">What We Offer</h2>
            <p>
              At Bliss Sierra, our services are built around real business needs.
              We combine strategy, engineering, design, and protection to create
              digital solutions that are secure, scalable, and valuable.
            </p>
            <p style={{ marginTop: "12px" }}>
              Whether you need stronger cybersecurity, a modern website,
              high-quality frontend development, or robust engineering support,
              we deliver solutions that help your brand move forward confidently.
            </p>
          </div>
        </div>
      </section>

      {/* ── TUNGA AEROSPACE COLLABORATION - MINIMIZED ── */}
      <section className="tunga-section-minimal">
        <div className="tunga-shell-minimal reveal-up">
          <div className="tunga-badge">
            <span className="badge-dot" />
            COLLABORATION
          </div>

          <h2 className="tunga-heading">
            Partnering with <span>Tunga Aerospace</span> for Defence UAV Solutions
          </h2> 

          <p className="tunga-subtext">
            Bliss Sierra collaborates with Tunga Aerospace Industries, delivering advanced UAV solutions 
            for surveillance, patrol, ISR, and tactical operations across police, BSF, Army, and disaster management.
          </p>

          <div>
            <p className="section-mini" style={{ marginBottom: "10px" }}>DEPLOYMENT DOMAINS</p>
            <div className="tunga-apps">
              {["Police", "BSF", "CRPF", "Army", "Disaster Management", "Forest Surveillance"].map((app) => (
                <span className="tunga-app-pill" key={app}>{app}</span>
              ))}
            </div>
          </div>

          <p className="section-mini" style={{ marginBottom: "10px" }}>DRISHYA SERIES — PRODUCT PORTFOLIO</p>
          <div className="tunga-table-wrap">
            <table className="tunga-table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Endurance</th>
                  <th>Range</th>
                  <th>Category</th>
                </tr>
              </thead>
              <tbody>
                {tungaDrones.map((drone) => (
                  <tr key={drone.model}>
                    <td>{drone.model}</td>
                    <td>{drone.endurance}</td>
                    <td>{drone.range}</td>
                    <td><span className="tunga-category-badge">{drone.category}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── BANKING SOLUTIONS - MINIMIZED ── */}
      <section className="banking-section-minimal">
        <div className="banking-shell-minimal reveal-up">
          <div className="banking-badge">
            <span className="badge-dot" />
            FINTECH SOLUTIONS
          </div>

          <h2 className="banking-heading">
            Banking & <span>Financial Technology</span>
          </h2>

          <p className="banking-subtext">
            Bliss Sierra specializes in cutting-edge banking technology for financial institutions, challenger banks, 
            and enterprises. We deliver FAPI-compliant platforms, AI agents, and secure fintech infrastructure.
          </p>

          <div className="banking-grid">
            {bankingProducts.map((product, index) => (
              <div className="banking-card" key={index}>
                <h4>{product.title}</h4>
                <p>{product.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CORE SERVICE LINES ── */}
      <section className="services-grid-wrap">
        <p className="section-mini">SERVICE AREAS</p>
        <h2 className="section-heading title-glow">Core Service Lines</h2>
        <div className="services-grid">
          {serviceCards.map((item, index) => (
            <div className="service-card reveal-up" key={index}>
              <div className="card-icon">{item.icon}</div>
              <div className="service-image">
                <img src={item.image} alt={item.title} />
              </div>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW WE DELIVER ── */}
      <section className="process-section" ref={processRef}>
        <p className="section-mini">OUR PROCESS</p>
        <h2 className="section-heading title-glow">How We Deliver</h2>
        <div className="process-grid">
          {[
            { num: "01", title: "Understand", text: "We begin by understanding your business, challenges, audience, and goals." },
            { num: "02", title: "Build", text: "We design and develop practical, elegant, and scalable solutions for your needs." },
            { num: "03", title: "Support", text: "We optimize, secure, and support your systems for long-term performance and growth." },
          ].map((step, i) => (
            <div className="process-point reveal-up" key={i}>
              <div className="process-number">{step.num}</div>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="tunga-footer">
        <div className="tunga-footer-content">
          <div className="tunga-footer-col reveal-left">
            <h3>Bliss Sierra</h3>
            <p>We are committed to delivering the best services for your business.</p>
          </div>
          <div className="tunga-footer-col reveal-up">
            <h3>Quick Links</h3>
            <NavLink to="/">Home</NavLink><br/>
            <NavLink to="/Tunga-Drones">Tunga Drones</NavLink><br/>
            <NavLink to="/Our-Projects">Our Projects</NavLink><br/>
            <NavLink to="/Services">Services</NavLink><br/>
            <NavLink to="/Careers">Careers</NavLink><br/>
            <NavLink to="/OurTeam">Our Core Team</NavLink>
          </div>
          <div className="tunga-footer-col reveal-right">
            <h3>Contact</h3>
            <p>📍 Neelakanta Nilayam, Kakateeya Hiils, Road No 9, Madhapur, Hyderabad-500081</p>
            <p>📧 contact@blisssierra.com</p>
            <p>📞 +91-7702232247</p>
            <div className="tunga-social-links">
              <a href="https://www.facebook.com/profile.php?id=61584931498008" target="_blank" rel="noreferrer" aria-label="Facebook"><FaFacebookF /></a>
              <a href="https://www.instagram.com/blisssierra1/" target="_blank" rel="noreferrer" aria-label="Instagram"><FaInstagram /></a>
              <a href="https://www.linkedin.com/feed/" target="_blank" rel="noreferrer" aria-label="LinkedIn"><FaLinkedin /></a>
              <a href="https://wa.me/917702232247" target="_blank" rel="noreferrer" aria-label="WhatsApp"><FaWhatsapp /></a>
            </div>
          </div>
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "30px", textAlign: "center", color: "rgba(255,255,255,0.6)" }}>
          <p>&copy; 2026 Bliss Sierra. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlissSierra;